import React, { useState, useEffect, useRef, useCallback } from "react";
import badgeProcess from "../../../assets/images/underVerification.svg";
import newChatIcon from "../../../assets/images/newChatIcon.png";
import { reset } from "../../../features/slices/PaymentSlice";
import DeleteBankDetail from "../../common/DeleteBankDetail";
import {
  closePaymentModal,
  openPaymentCompletionModal,
  openPaymentModal,
} from "../../../features/slices/ModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  showChatBoxInDesktop,
  updateSelectedItem,
} from "../../../features/slices/CommonSlice";
import {
  showAddBankModal,
  closeAddBankModal,
  showAddUpiModal,
  closeAddUpiModal,
} from "../../../features/slices/ProfileSlice";
import BankAccountAddedPopup from "../../common/BankAccountAddedPopup";
import {
  createWithdrawRequest,
  getBankAccounts,
  getUpiAccounts,
  setPrimaryBankAccount,
} from "../../../features/slices/PaymentSlice";
import ErrorCard from "../../common/ErrorCard";
import {
  getBalanceCard,
  getUserData,
} from "../../../features/slices/UserSlice";
import NewBankAccount from "../../profile/NewBankAccount";
import NewUpiId from "../../profile/NewUpiId";
import WithdrawConfirmPopup from "./WithdrawConfirmPopup";
export default function WithdrawContent() {
  const dispatch = useDispatch();
  const amountOptions = [
    {
      amount: "500",
      id: "amount1",
    },
    {
      amount: "1,000",
      id: "amount2",
    },
    {
      amount: "5,000",
      id: "amount3",
    },
    {
      amount: "10,000",
      id: "amount4",
    },
    {
      amount: "50,000",
      id: "amount5",
    },
  ];
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const payment = useSelector((state) => state.payment);
  const user = useSelector((state) => state.user);
  const profile = useSelector((state) => state.profile);

  const [enteredAmount, setAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState("amount1");
  const [redBorder, setRedBorder] = useState(false);
  const [localError, setLocalError] = useState("");
  const [verifiedBanks, setVerifiedBanks] = useState([]);
  const [unVerifiedBanks, setUnVerifiedBanks] = useState([]);
  const [verifiedUpis, setVerifiedUpis] = useState([]);
  const [unVerifiedUpis, setUnVerifiedUpis] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedUpiId, setSelectedUpiId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [accountTobeDeleted, setAccountTobeDeleted] = useState({});
  const [deleteAccountType, setDeleteAccountType] = useState("bank");
  const [bankAdded, setBankAdded] = useState(false);
  const [upiAdded, setUpiAdded] = useState(false);
  const [canScrollMore, setCanScrollMore] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const bankListsRef = useRef(null);

  const updateScrollHint = useCallback(() => {
    const el = bankListsRef.current;
    if (!el || isMobile) {
      setCanScrollMore(false);
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    setCanScrollMore(el.scrollHeight > el.clientHeight + 8 && remaining > 12);
  }, [isMobile]);

  useEffect(() => {
    const id = requestAnimationFrame(() => updateScrollHint());
    return () => cancelAnimationFrame(id);
  }, [
    updateScrollHint,
    verifiedBanks,
    unVerifiedBanks,
    verifiedUpis,
    unVerifiedUpis,
    localError,
    payment.error,
  ]);

  useEffect(() => {
    window.addEventListener("resize", updateScrollHint);
    return () => window.removeEventListener("resize", updateScrollHint);
  }, [updateScrollHint]);

  // GET /player/balance; falls back to the /player/me figures until it loads
  const balanceCard = user.balanceCard;
  const totalBalance = balanceCard
    ? balanceCard.totalBalance
    : user.userData.totalBalance;
  const withdrawableBalance = balanceCard
    ? balanceCard.withdrawableBalance
    : user.userData.redisBalance ?? user.userData.totalBalance;
  const bonusBalance = balanceCard
    ? balanceCard.bonusBalance
    : user.userData.bonus;
  const withdrawBlockedReason = balanceCard?.realWalletFrozen
    ? "Your wallet is frozen, so withdrawals are unavailable. Please contact support."
    : user.userData.allowWithdrawals === false
    ? "Withdrawals are disabled for your account. Please contact support."
    : "";

  const amountValue = +String(enteredAmount).replace(/,/g, "");
  const amountError = !enteredAmount
    ? ""
    : amountValue < 100
    ? "Amount can't be less than 100"
    : amountValue > 500000
    ? "Amount can't be greater than 500,000"
    : amountValue > (Number(withdrawableBalance) || 0)
    ? "Amount can't be more than your withdrawable balance"
    : "";
  const isAmountValid = Boolean(enteredAmount) && !amountError;
  const hasDestination = Boolean(selectedBank || selectedUpiId);
  // a payout account is mandatory: Next stays disabled until one is selected
  const canProceed = isAmountValid && hasDestination && !withdrawBlockedReason;

  const goToNextStep = () => {
    if (!isAmountValid) {
      setRedBorder(true);
      return;
    } else if (!verifiedBanks.length && !verifiedUpis.length) {
      setLocalError("Please add atleast one verified bank account or UPI ID");
      setTimeout(() => {
        setLocalError("");
      }, 2000);
      return;
    } else if (!selectedBank && !selectedUpiId) {
      setLocalError("Please select a bank account or UPI ID");
      return;
    }
    setShowConfirm(true);
  };

  const confirmWithdraw = () => {
    setShowConfirm(false);
    dispatch(
      createWithdrawRequest({
        amount: enteredAmount,
        bankId: selectedBank || selectedUpiId,
      })
    );
  };

  const getSelectedDestination = () => {
    const bank = verifiedBanks.find((item) => item.bankId === selectedBank);
    if (bank) {
      return `${bank.bankName} ${bank.accountNumberMasked || ""}`.trim();
    }
    const upi = verifiedUpis.find(
      (item) => item.upiAccountId === selectedUpiId
    );
    return upi ? upi.upiId || upi.accountName : "";
  };

  // why Next is disabled, shown before any amount is typed
  const hasAnyAccount =
    verifiedBanks.length ||
    unVerifiedBanks.length ||
    verifiedUpis.length ||
    unVerifiedUpis.length;
  const accountNotice =
    verifiedBanks.length || verifiedUpis.length
      ? ""
      : hasAnyAccount
      ? "Your bank accounts and UPI IDs are under verification. You can withdraw once one is verified."
      : "Add a bank account or UPI ID to withdraw.";

  const handleAmountSelect = (amount) => {
    const prevAmount = +enteredAmount.replace(/,/g, "");
    const newAmount = +amount.replace(/,/g, "");
    const addedAMount = prevAmount + newAmount;
    let finalAmount = (
      parseInt(addedAMount.toString().replace(/[^\d]+/gi, "")) || ""
    ).toLocaleString("en-US");
    if (finalAmount.length === 7) {
      finalAmount = finalAmount.slice(0, 1) + "," + finalAmount.slice(1);
    }
    setAmount(finalAmount);
  };

  const addNewBankAccount = () => {
    // dispatch(closePaymentModal());
    dispatch(showAddBankModal());
  };

  const addNewUpiAccount = () => {
    dispatch(showAddUpiModal());
  };

  const openDepositScreen = () => {
    if (!isMobile) dispatch(openPaymentModal("Deposit"));
    else dispatch(updateSelectedItem("deposit"));
  };

  const handleBankSelect = (item) => {
    setSelectedBank(item.bankId);
    setSelectedUpiId(null);
    setLocalError("");
    if (!item.isPrimary) {
      dispatch(setPrimaryBankAccount(item.bankId));
    }
  };

  const handleUpiSelect = (item) => {
    if (!item.isVerified) return;
    setSelectedUpiId(item.upiAccountId);
    setSelectedBank(null);
    setLocalError("");
  };

  useEffect(() => {
    dispatch(getBalanceCard());
    dispatch(getBankAccounts());
    dispatch(getUpiAccounts());
  }, [dispatch]);

  useEffect(() => {
    const accounts = payment.bankAccounts || [];
    const verifedOnes = accounts.filter((item) => item.isVerified);
    const unVerifiedOnes = accounts.filter((item) => !item.isVerified);
    setVerifiedBanks(verifedOnes);
    setUnVerifiedBanks(unVerifiedOnes);

    if (!verifedOnes.length) {
      setSelectedBank(null);
      return;
    }

    setSelectedBank((prev) => {
      if (prev && verifedOnes.some((item) => item.bankId === prev)) return prev;
      if (selectedUpiId) return null;
      const defaultBank =
        verifedOnes.find((item) => item.isPrimary) || verifedOnes[0];
      return defaultBank.bankId;
    });
    // Only re-default when bank list changes; selectedUpiId is read for mutual exclusion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.bankAccounts]);

  useEffect(() => {
    const accounts = payment.upiAccounts || [];
    const verifiedOnes = accounts.filter((item) => item.isVerified);
    const unverifiedOnes = accounts.filter((item) => !item.isVerified);
    setVerifiedUpis(verifiedOnes);
    setUnVerifiedUpis(unverifiedOnes);

    setSelectedUpiId((prev) => {
      if (prev && verifiedOnes.some((item) => item.upiAccountId === prev)) {
        return prev;
      }
      return null;
    });
  }, [payment.upiAccounts]);

  useEffect(() => {
    if (payment.bankRemoved) {
      dispatch(getBankAccounts());
    }
  }, [payment.bankRemoved, dispatch]);

  useEffect(() => {
    if (payment.upiRemoved) {
      dispatch(getUpiAccounts());
    }
  }, [payment.upiRemoved, dispatch]);

  useEffect(() => {
    if (payment.withrawRequestedSuccessfully) {
      dispatch(getUserData({ source: "withdraw_request", showLoader: true }));
      dispatch(getBalanceCard());
      if (isMobile) dispatch(updateSelectedItem("withdrawCompleted"));
      else {
        dispatch(closePaymentModal());
        dispatch(openPaymentCompletionModal("Withdrawl"));
      }
    }
  }, [payment.withrawRequestedSuccessfully]);

  useEffect(() => {
    if (isMobile)
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
  }, [payment.error]);

  const setEnteredAmount = (event) => {
    let amount = (
      parseInt(event.target.value.replace(/[^\d]+/g, "")) || ""
    ).toLocaleString("en-US");
    if (amount.length === 7) {
      amount = amount.slice(0, 1) + "," + amount.slice(1);
    }
    setAmount(amount);
    setRedBorder(false);
  };
  const getFormattedBalance = (balance) => {
    if (balance) {
      if (typeof balance === "number") return balance.toLocaleString();
      if (!balance.includes(",")) {
        const intBalance = parseInt(balance);
        return intBalance.toLocaleString();
      }
    }
    return "0";
  };

  const showChatBox = () => {
    if (isMobile) {
      dispatch(updateSelectedItem("chat-bot"));
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      dispatch(showChatBoxInDesktop());
      dispatch(closePaymentModal());
    }
  };
  const removeBankAccount = (item) => {
    setDeleteAccountType("bank");
    setShowDelete(true);
    setAccountTobeDeleted(item);
  };
  const removeUpiAccount = (item) => {
    setDeleteAccountType("upi");
    setShowDelete(true);
    setAccountTobeDeleted(item);
  };
  const closeModal = () => {
    setShowDelete(false);
    setDeleteAccountType("bank");
    dispatch(reset());
  };
  const closeBankSuccessModal = () => {
    setBankAdded(false);
  };
  const closeUpiSuccessModal = () => {
    setUpiAdded(false);
  };
  useEffect(() => {
    if (payment.bankDetails) {
      dispatch(getBankAccounts());
      dispatch(closeAddBankModal());
      setBankAdded(true);
    }
  }, [payment.bankDetails, dispatch]);
  useEffect(() => {
    if (payment.upiDetails) {
      dispatch(getUpiAccounts());
      dispatch(closeAddUpiModal());
      setUpiAdded(true);
      dispatch(reset());
    }
  }, [payment.upiDetails, dispatch]);
  return (
    <>
      {bankAdded && (
        <BankAccountAddedPopup closeModal={closeBankSuccessModal} />
      )}
      {upiAdded && (
        <BankAccountAddedPopup
          closeModal={closeUpiSuccessModal}
          title="UPI ID added successfully."
          subtitle="UPI ID will be available after verification."
        />
      )}
      {showConfirm && (
        <WithdrawConfirmPopup
          amount={enteredAmount}
          destination={getSelectedDestination()}
          onConfirm={confirmWithdraw}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      {profile.addBankModal && <NewBankAccount />}
      {profile.addUpiModal && <NewUpiId />}
      {showDelete && (
        <DeleteBankDetail
          accountInfo={accountTobeDeleted}
          closeModal={closeModal}
          accountType={deleteAccountType}
        />
      )}
      <div className="row">
        <div
          className="col-md-6 col-lg-6 col-xl-6 col-sm-12"
          style={{ borderRight: "1px solid #70707071" }}
        >
          {payment.error && isMobile ? (
            <ErrorCard error={payment.error} />
          ) : null}
          <div className="withdrawl-left mt-4" style={{ padding: "0 1.6rem" }}>
            <div className="balance-info py-2 px-3 text--black mb-3">
              <div
                className="balance d-flex m-auto justify-content-around"
                style={{ height: "75px" }}
              >
                <div className="total-bal">
                  <div>
                    <p className="text--normal text-center mb-1">Total Bal.</p>
                    <p className="mb-0">
                      <b>
                        ₹
                        {getFormattedBalance(totalBalance)}
                      </b>
                    </p>
                  </div>
                </div>
                <div className="withdrawl-bal">
                  <div>
                    <p className="text--normal text-center mb-1">
                      Withdrawl Bal.
                    </p>
                    <p className="mb-0">
                      <b>
                        ₹
                        {getFormattedBalance(withdrawableBalance)}
                      </b>
                    </p>
                  </div>
                </div>
                <div className="bonus-bal">
                  <div>
                    <p className="text--normal text-center mb-1">Bonus</p>
                    <p className="mb-0">
                      <b>₹{getFormattedBalance(bonusBalance)}</b>
                    </p>
                  </div>
                </div>
              </div>
              {/* <div className="bal-progress mb-3">
                <div className="circle--progress"></div>
                <span className="progress-value text--normal">
                  <strong>72.4%</strong>
                </span>
              </div> */}
            </div>
          </div>
          <div className="amount-input my-3 px-4">
            <label className="text--small">
              Amount <span aria-hidden="true" className="required-asterisk">*</span>
            </label>
            <div className="d-flex">
              <span style={{ float: "start" }}>INR</span>
              <input
                type="text"
                className={redBorder ? "red-border" : ""}
                placeholder="Enter Amount"
                pattern="^[\d,]+$"
                value={enteredAmount}
                onChange={(e) => {
                  setEnteredAmount(e);
                }}
              />
            </div>
            <p className="low-opacity text--small mb-0 mt-1">100-5,00,000</p>
          </div>
          {/* <div className="amount-options my-3 px-4">
            <div className="d-flex">
              {amountOptions.map((item, index) => (
                <div
                  key={index}
                  className="text-center amount"
                  id={item.id}
                  onClick={() => handleAmountSelect(item.amount)}
                >
                  +{item.amount}
                </div>
              ))}
            </div>
          </div> */}
          <div className="px-4">
            <p
              className="low-opacity text--small mb-0 mt-1"
              style={{ color: "#E42D2D" }}
            >
              {amountError}
            </p>
          </div>
          {!isMobile && (
            <div className="m-4">
              <p className="text--medium">
                Want to win a Deposit Bonus?{" "}
                <b
                  style={{ color: "#F5CA3D", cursor: "pointer" }}
                  onClick={openDepositScreen}
                >
                  Deposit Now
                </b>
              </p>
              <img
                loading="lazy"
                onClick={openDepositScreen}
                className="w-100 mb-3"
                src={require("../../../assets/images/casino.webp")}
                alt="casino"
              />
            </div>
          )}
        </div>
        <div className="col-md-6 col-lg-6 col-xl-6 col-sm-12 withdraw-accounts-col">
          <p className="mt-3 px-3 text--normal withdraw-accounts-heading">
            Saved Bank Accounts for withdrawals{" "}
            <span aria-hidden="true" className="required-asterisk">*</span>
          </p>
          <div
            className={`bank-lists-wrap${canScrollMore ? " bank-lists-wrap--more" : ""}`}
          >
            <div
              className="mb-3 px-3 bank-lists"
              ref={bankListsRef}
              onScroll={updateScrollHint}
            >
            <div className="added-banks-list">
              {verifiedBanks.map((item) => (
                <div className="form-check mb-1" key={item.bankId}>
                  <div className="d-flex checkbox-div">
                    <input
                      className="bank-checkbox-input"
                      type="radio"
                      name="withdrawDestination"
                      value={item.bankName}
                      id={`bank-${item.bankId}`}
                      checked={selectedBank === item.bankId}
                      onChange={() => handleBankSelect(item)}
                    />
                    <label
                      className="text--normal form-check-label mx-2"
                      htmlFor={`bank-${item.bankId}`}
                    >
                      {item.bankName}
                    </label>
                  </div>
                  <p
                    className="text--small mx-2 mt-1 mb-1"
                    style={{ fontWeight: "200", paddingLeft: "17px" }}
                  >
                    Account number Ending with {item.accountNumberMasked}
                  </p>
                  <button
                    className="mb-3 delete-bank"
                    style={{
                      color: "#FF1515",
                      marginLeft: "17px",
                      width: "60px",
                      background: "none",
                      border: "none",
                      fontSize: "14px",
                    }}
                    onClick={() => removeBankAccount(item)}
                  >
                    Delete
                  </button>
                  {item.isPrimary && (
                    <div className="primary-account">
                      <i className="bi bi-check2 mx-2"></i>
                      <span>Primary</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!verifiedBanks.length && !unVerifiedBanks.length ? (
              <p className="text--small low-opacity mb-2">
                No saved bank accounts yet.
              </p>
            ) : null}
            {unVerifiedBanks.map((item) => (
              <div
                className="mt-2 banks-to-be-added d-flex mb-2"
                key={item.bankId}
              >
                <img
                  loading="lazy"
                  src={require("../../../assets/images/lock.png")}
                  width={20}
                  height={28}
                  alt="lock"
                  style={{ marginTop: "-5px" }}
                />
                {/* <i className="bi bi-lock-fill"></i> */}
                <div className="mx-2">
                  <div className="d-flex">
                    <p
                      className="text--normal form-check-label mb-2"
                    >
                      {item.bankName}
                    </p>
                    <img
                      loading="lazy"
                      className="mx-3"
                      src={badgeProcess}
                      alt="in process"
                    />
                  </div>
                  <p className="text--small" style={{ fontWeight: "200" }}>
                    Account number Ending with {item.accountNumberMasked}
                  </p>
                </div>
              </div>
            ))}

            <p
              className="text--normal add-bank-account-text"
              style={{ color: "#29B3EF", cursor: "pointer" }}
              onClick={addNewBankAccount}
            >
              + Add Bank Account
            </p>

            <p className="mt-4 text--normal">Saved UPI ID for withdraws</p>
            <div className="added-banks-list">
              {verifiedUpis.map((item) => (
                <div className="form-check mb-3" key={item.upiAccountId}>
                  <div className="d-flex checkbox-div align-items-center">
                    <input
                      className="bank-checkbox-input"
                      type="radio"
                      name="withdrawDestination"
                      value={item.upiAccountId}
                      id={`upi-${item.upiAccountId}`}
                      checked={selectedUpiId === item.upiAccountId}
                      onChange={() => handleUpiSelect(item)}
                    />
                    <label
                      className="text--normal form-check-label mx-2 mb-0"
                      htmlFor={`upi-${item.upiAccountId}`}
                    >
                      {item.upiId || item.accountName}
                    </label>
                  </div>
                  {(item.accountHolderName || item.accountName) && (
                    <p
                      className="text--small mx-2 mt-1 mb-0"
                      style={{ fontWeight: "200", paddingLeft: "17px" }}
                    >
                      {item.accountHolderName || item.accountName}
                    </p>
                  )}
                  <button
                    className="delete-bank delete-upi"
                    style={{
                      color: "#FF1515",
                      background: "none",
                      border: "none",
                      fontSize: "14px",
                    }}
                    onClick={() => removeUpiAccount(item)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
            {!verifiedUpis.length && !unVerifiedUpis.length ? (
              <p className="text--small low-opacity mb-2">
                No saved UPI IDs yet.
              </p>
            ) : null}
            {unVerifiedUpis.map((item) => (
              <div
                className="mt-2 banks-to-be-added d-flex mb-2"
                key={item.upiAccountId}
              >
                <img
                  loading="lazy"
                  src={require("../../../assets/images/lock.png")}
                  width={20}
                  height={28}
                  alt="lock"
                  style={{ marginTop: "-5px" }}
                />
                <div className="mx-2">
                  <div className="d-flex">
                    <p className="text--normal form-check-label mb-2">
                      {item.upiId || item.accountName}
                    </p>
                    <img
                      loading="lazy"
                      className="mx-3"
                      src={badgeProcess}
                      alt="in process"
                    />
                  </div>
                  <p className="text--small" style={{ fontWeight: "200" }}>
                    {item.accountHolderName || item.accountName || item.verificationStatus}
                  </p>
                </div>
              </div>
            ))}

            <p
              className="text--normal add-bank-account-text"
              style={{ color: "#29B3EF", cursor: "pointer" }}
              onClick={addNewUpiAccount}
            >
              + Add UPI ID
            </p>

            {payment.error && !isMobile ? (
              <ErrorCard error={payment.error} />
            ) : null}

            {withdrawBlockedReason ? (
              <p className="text--small mt-3 mb-0" style={{ color: "#E42D2D" }}>
                {withdrawBlockedReason}
              </p>
            ) : null}

            {accountNotice && !withdrawBlockedReason ? (
              <p className="text--small mt-3 mb-0" style={{ color: "#F5CA3D" }}>
                <i className="bi bi-info-circle"></i> {accountNotice}
              </p>
            ) : null}

            {!accountNotice && isAmountValid && !hasDestination && !localError ? (
              <p className="text--small mt-3 mb-0" style={{ color: "#E42D2D" }}>
                Select a bank account or UPI ID to withdraw
              </p>
            ) : null}

            {localError ? (
              <div
                className="mt-4 p-3 payment-error border border-light border-opacity-25 rounded"
                style={{
                  color: "rgb(158, 109, 29)",
                  background: "rgb(20,20,20)",
                }}
              >
                <p className="text--medium mb-0">
                  <i className="bi bi-info-circle"></i> {localError}
                </p>
              </div>
            ) : null}
            </div>
            {canScrollMore ? (
              <div className="bank-lists-scroll-hint" aria-hidden="true">
                <span>Scroll for more</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            ) : null}
          </div>
          <div className="action-btns px-3">
            {/* <p className="text-center text--medium statement">
              Problem in making payment please contact customer support
            </p> */}
            <div className="d-flex justify-content-between">
              <button
                className="border-btn"
                style={{ color: "#F5CA3D", border: "1px solid #F5CA3D" }}
                onClick={showChatBox}
              >
                <img
                  loading="lazy"
                  src={newChatIcon}
                  width={isMobile ? 25 : 30}
                  alt="logo"
                  className="mx-1"
                />
                Chat with us
              </button>
              <button
                className="standard-btn"
                onClick={goToNextStep}
                disabled={!canProceed}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
